import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL, STATUS_LABELS, MONTHS, formatDateOnly } from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminUserApproval() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  useEffect(() => {
    let filtered = [...pendingUsers];

    if (monthFilter) {
      filtered = filtered.filter((u) => {
        const d = u.createdAt ? new Date(u.createdAt) : null;
        return d && d.getMonth() === parseInt(monthFilter);
      });
    }

    if (yearFilter) {
      filtered = filtered.filter((u) => {
        const d = u.createdAt ? new Date(u.createdAt) : null;
        return d && d.getFullYear() === parseInt(yearFilter);
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (u) =>
          (u.firstName || "").toLowerCase().includes(q) ||
          (u.lastName || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (u.phone || "").toLowerCase().includes(q) ||
          (u.loginId || "").toLowerCase().includes(q) ||
          (u.role || "").toLowerCase().includes(q)
      );
    }

    setFilteredUsers(filtered);
  }, [monthFilter, yearFilter, searchQuery, pendingUsers]);

  const fetchPendingUsers = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      const res = await fetch(`${API_BASE_URL}/admin/pending-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setPendingUsers(data.users || []);
        setFilteredUsers(data.users || []);
      }
    } catch (err) {
      console.error("Fetch pending users error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/admin/approve-user/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`User approved successfully.`);
        setPendingUsers((prev) => prev.filter((u) => u.id !== id));
      } else {
        setMessage(data.error || "Failed to approve.");
      }
    } catch (err) {
      setMessage("Server error.");
    }
  };

  const handleReject = async (id) => {
    const token = sessionStorage.getItem("tvc_token");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/admin/reject-user/${id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(`User rejected.`);
        setPendingUsers((prev) => prev.filter((u) => u.id !== id));
      } else {
        setMessage(data.error || "Failed to reject.");
      }
    } catch (err) {
      setMessage("Server error.");
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);

  if (loading) {
    return <div className="AdminEmptyState">Loading pending users...</div>;
  }

  return (
    <div>
      {message && (
        <div style={{ marginBottom: 16 }}>
          <p className="AdminSuccess">{message}</p>
        </div>
      )}

      <div className="AdminCard">
        <div className="AdminCardHeader">
           <h3 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 20, fontWeight: 700, color: "#1B2A4A" }}>Pending User Approvals <span className="AdminCountBadge">{filteredUsers.length}</span></h3>
          <div className="AdminPillFilterBar">
            <div className="AdminPillGroup">
              <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
                <span className="AdminPillSearchIcon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  ref={searchRef}
                  type="text"
                  className="AdminPillSearch"
                  placeholder="Search name, email, login ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                   onBlur={() => { setSearchQuery(""); setTimeout(() => setSearchOpen(false), 200) }}
                />
                {!searchOpen && (
                  <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
                )}
              </div>
            </div>

            <div className="AdminPillGroup">
              <div className="AdminPillFilterItem">
                <select className="AdminPillSelect" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
                  <option value="">Months</option>
                  {MONTHS.map((m, i) => (
                    <option key={i} value={i}>{m}</option>
                  ))}
                </select>
                {monthFilter && (
                  <button className="AdminPillClear" onClick={() => setMonthFilter("")}>&times;</button>
                )}
              </div>

              <div className="AdminPillFilterItem">
                <select className="AdminPillSelect" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
                  <option value="">Years</option>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                {yearFilter && (
                  <button className="AdminPillClear" onClick={() => setYearFilter("")}>&times;</button>
                )}
              </div>
            </div>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="AdminEmptyState">No pending user approvals.</div>
        ) : (
          <div className="AdminTableScroll">
          <table className="AdminTable">
            <thead>
              <tr>
                <th>Date</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Login ID</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td>{formatDateOnly(u.createdAt)}</td>
                  <td>{u.firstName} {u.lastName}</td>
                  <td>{u.email}</td>
                  <td>{u.countryCode} {u.phone}</td>
                  <td>
                    <span className={`AdminRoleIndicator AdminRole${u.role}`}>
                      {u.role}
                    </span>
                  </td>
                  <td>{u.loginId}</td>
                  <td>
                    <span className={`AdminBadge AdminBadge${u.status === "active" ? "Active" : u.status === "pending" ? "Pending" : "Rejected"}`}>
                      {STATUS_LABELS[u.status] || u.status}
                    </span>
                  </td>
                  <td>
                    <div className="AdminActionsCell">
                      <button
                        className="AdminButton AdminButtonSmall AdminButtonSuccess"
                        onClick={() => handleApprove(u.id)}
                      >
                        Approve
                      </button>
                      <button
                        className="AdminButton AdminButtonSmall AdminButtonDanger"
                        onClick={() => handleReject(u.id)}
                      >
                        Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUserApproval;
